// useAuth — read the current sign-in state and expose helpers to
// request/verify/sign-out. Wraps the magic-link + OAuth endpoints from
// the api-gateway. The hook is **read-mostly** at the generated-app
// level: most apps only need to know `signedIn` and `accountId` to
// decide whether to show a "Sign in to sync" prompt or to use the
// signed-in account scope for state sync.
//
// State is cached in an HMR-stable module singleton so multiple
// components calling useAuth() share the same source of truth without
// re-fetching /v1/auth/me on every mount.

import { useCallback, useEffect, useState } from 'preact/hooks';

export interface AuthSession {
  signedIn: boolean;
  accountId?: string;
  email?: string;
  /** Provider used to sign in: 'magic-link' | 'apple' | 'google'. */
  provider?: 'magic-link' | 'apple' | 'google';
  /** Token issuance timestamp (ms). */
  issuedAt?: number;
  /** Expiry timestamp (ms). */
  expiresAt?: number;
  /** Account creation timestamp (ms) — for the Account screen. */
  memberSince?: number;
  /** Count of devices linked to this account. */
  devices?: number;
  /** Count of apps this account has created. */
  appsCreated?: number;
  /** Current subscription plan — defaults to 'free' when absent. */
  plan?: 'free' | 'pro';
  /**
   * Founder god-mode — true ONLY when the verified session email matches
   * FOUNDER_EMAIL. Bypasses every generation ceiling and rate limit.
   * Server-derived; clients cannot set this.
   */
  isAdmin?: boolean;
}

export interface RequestLinkResult {
  /** True on any 2xx response — including the dev/founder fail-soft path. */
  ok: boolean;
  /** Set by the gateway in non-prod when Resend isn't configured: surfaces
   *  the OTP back to the client so the dev can complete sign-in without
   *  combing through the worker terminal. Always undefined in production. */
  devCode?: string;
  /** True when the gateway reports the degraded dev/no-Resend path. */
  devMode?: boolean;
}

export interface UseAuthResult {
  session: AuthSession;
  loading: boolean;
  /** POST /v1/auth/request-link. Returns the response status + any dev
   *  fail-soft hints. The boolean `.ok` is the only field UIs need to
   *  advance to the code step — `devCode`/`devMode` are surfaced for UX
   *  affordances (e.g. inline "Use code XXXXXX" in dev). */
  requestLink: (email: string) => Promise<RequestLinkResult>;
  /** POST /v1/auth/verify. Returns the new session on success. */
  verifyCode: (email: string, code: string) => Promise<AuthSession | null>;
  /** POST /v1/auth/sign-out. Returns true on 200. */
  signOut: () => Promise<boolean>;
  /** Force a refresh of /v1/auth/me. */
  refresh: () => Promise<AuthSession>;
}

// Module-level cache to avoid re-fetching /me on every mount.
let cachedSession: AuthSession = { signedIn: false };
let cachedFetched = false;
const listeners = new Set<(s: AuthSession) => void>();

function setCachedSession(next: AuthSession): void {
  cachedSession = next;
  for (const l of listeners) l(next);
}

async function fetchMe(): Promise<AuthSession> {
  try {
    const res = await fetch('/v1/auth/me', { credentials: 'include' });
    if (!res.ok) return { signedIn: false };
    const body = (await res.json()) as {
      signedIn: boolean;
      accountId?: string;
      email?: string;
      issuedAt?: number;
      expiresAt?: number;
      memberSince?: number;
      devices?: number;
      appsCreated?: number;
      plan?: string;
      isAdmin?: boolean;
    };
    return {
      signedIn: !!body.signedIn,
      accountId: body.accountId,
      email: body.email,
      issuedAt: body.issuedAt,
      expiresAt: body.expiresAt,
      memberSince: body.memberSince,
      devices: body.devices,
      appsCreated: body.appsCreated,
      plan: body.plan === 'pro' ? 'pro' : 'free',
      isAdmin: body.isAdmin === true
    };
  } catch {
    return { signedIn: false };
  }
}

function getDeviceId(): string {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(/(?:^|; )vs_did=([^;]+)/);
  return match && match[1] ? decodeURIComponent(match[1]) : '';
}

export function useAuth(): UseAuthResult {
  const [session, setSession] = useState<AuthSession>(cachedSession);
  const [loading, setLoading] = useState<boolean>(!cachedFetched);

  useEffect(() => {
    const onChange = (s: AuthSession) => setSession(s);
    listeners.add(onChange);
    if (!cachedFetched) {
      cachedFetched = true;
      // fetchMe already wraps its body in try/catch and resolves with
      // {signedIn:false} on any failure — it should never reject. The
      // explicit .catch here is belt-and-suspenders so a future refactor
      // that drops the inner try/catch can never strand the UI in a
      // permanent loading state.
      fetchMe()
        .then((s) => {
          setCachedSession(s);
          setLoading(false);
        })
        .catch(() => {
          setCachedSession({ signedIn: false });
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    return () => {
      listeners.delete(onChange);
    };
  }, []);

  const requestLink = useCallback(async (email: string): Promise<RequestLinkResult> => {
    try {
      const res = await fetch('/v1/auth/request-link', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email })
      });
      if (!res.ok) return { ok: false };
      // The gateway returns 2xx for both the real-send and the dev/no-Resend
      // fail-soft path. The body distinguishes them via `dev_mode` and
      // optionally `devCode` so a dev UI can show the OTP inline. We tolerate
      // an empty / non-JSON body (network proxies sometimes strip it) and
      // still advance the user.
      let body: { dev_mode?: boolean; devCode?: string } = {};
      try {
        body = (await res.json()) as { dev_mode?: boolean; devCode?: string };
      } catch {
        /* empty body — treat as plain 2xx */
      }
      return {
        ok: true,
        devMode: body.dev_mode === true,
        devCode: typeof body.devCode === 'string' ? body.devCode : undefined
      };
    } catch {
      return { ok: false };
    }
  }, []);

  const verifyCode = useCallback(async (email: string, code: string): Promise<AuthSession | null> => {
    try {
      const res = await fetch('/v1/auth/verify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, code, deviceId: getDeviceId() })
      });
      if (!res.ok) return null;
      const body = (await res.json()) as {
        accountId: string;
        email: string;
        plan?: 'free' | 'pro';
        isAdmin?: boolean;
      };
      const next: AuthSession = {
        signedIn: true,
        accountId: body.accountId,
        email: body.email,
        // plan ships in the verify response (finding AUTH-H5) so the
        // cached session is complete immediately — no Pro-gated UI
        // flicker and no follow-up /v1/auth/me round-trip. Same applies
        // to isAdmin — the founder's Admin entries should appear in the
        // NavRail/Sidebar the instant verify succeeds, not after a
        // page reload.
        plan: body.plan,
        isAdmin: body.isAdmin === true,
        provider: 'magic-link',
        issuedAt: Date.now()
      };
      setCachedSession(next);
      return next;
    } catch {
      return null;
    }
  }, []);

  const signOut = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch('/v1/auth/sign-out', {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) setCachedSession({ signedIn: false });
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  const refresh = useCallback(async (): Promise<AuthSession> => {
    const s = await fetchMe();
    setCachedSession(s);
    return s;
  }, []);

  return { session, loading, requestLink, verifyCode, signOut, refresh };
}
